/*
 * Google reCAPTCHA Enterprise wiring for atlantis.
 *
 * This module is the single integration point between the CMS-provided
 * site key / open-proxy URL and the axios interceptor in `axiosService.ts`.
 * The interceptor calls `getRecaptchaTokenForUrl` for every outbound
 * request and — when the URL targets the open service calculator proxy —
 * receives a fresh, single-use token that is attached as the
 * `X-Recaptcha-Token` header.
 *
 * Why single-use / per-request generation?
 *   Google tokens expire after ~2 minutes and are intended to be
 *   consumed exactly once, so we never cache the token itself. We only
 *   cache the load-promise of the grecaptcha script.
 *
 * Why module-level state?
 *   The axios interceptor runs on every request; consulting Vuex on the
 *   hot path is unnecessary once the CMS values have been observed. The
 *   Vue root component calls `configureRecaptcha` whenever the site key
 *   or open-proxy URL changes, and everything else lives in this file.
 *
 * Fail-open policy:
 *   Any failure (SSR, missing site key, script load error, Google runtime
 *   error) resolves to `undefined` — never rejects — so calculator
 *   requests keep flowing even when reCAPTCHA is unavailable. Enforcement
 *   is the backend's responsibility.
 *
 * Reference: https://cloud.google.com/recaptcha-enterprise/docs/instrument-web-pages
 */

import { exception } from '@/appinsights/logging/ApplicationInsightLog';
import store from '@/store/store';

type GrecaptchaApi = {
	ready: (cb: () => void) => void;
	execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

declare global {
	interface Window {
		grecaptcha?: GrecaptchaApi & {
			enterprise?: GrecaptchaApi;
		};
	}
}

export const RECAPTCHA_HEADER = 'X-Recaptcha-Token';

const RECAPTCHA_SCRIPT_ID = 'recaptcha-enterprise-script';

let currentSiteKey: string | undefined;
let openProxyBaseUrl: string | undefined;
let readyPromise: Promise<void> | undefined;

export function configureRecaptcha(siteKey: string | undefined, openProxyBase: string | undefined): void {
	if (!siteKey || !openProxyBase) {
		return;
	}

	openProxyBaseUrl = openProxyBase;

	if (siteKey === currentSiteKey && readyPromise !== undefined) {
		return;
	}

	currentSiteKey = siteKey;
	store.dispatch('setRecaptchaSiteKey', siteKey);
	readyPromise = loadScript(siteKey);
}

export async function getRecaptchaTokenForUrl(url: string | undefined): Promise<string | undefined> {
	if (!isOpenProxyRequest(url)) {
		return undefined;
	}
	if (!currentSiteKey || readyPromise === undefined) {
		return undefined;
	}

	try {
		await readyPromise;
		const api = window.grecaptcha?.enterprise;
		if (!api?.execute) {
			return undefined;
		}
		return await api.execute(currentSiteKey, {
			action: sanitizeAction(url as string),
		});
	} catch (err) {
		exception(err, 'Failed reCAPTCHA token');
		return undefined;
	}
}

function loadScript(key: string): Promise<void> {
	return new Promise<void>((resolve, reject) => {
		if (typeof document === 'undefined') {
			reject(new Error('document not available'));
			return;
		}

		const existing = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;

		if (existing) {
			if (window.grecaptcha?.enterprise?.execute) {
				window.grecaptcha.enterprise.ready(() => resolve());
				return;
			}
			existing.addEventListener('load', () => {
				if (window.grecaptcha?.enterprise?.ready) {
					window.grecaptcha.enterprise.ready(() => resolve());
				} else {
					resolve();
				}
			});
			existing.addEventListener('error', () => reject(new Error('recaptcha script failed to load')));
			return;
		}

		const script = document.createElement('script');
		script.id = RECAPTCHA_SCRIPT_ID;
		script.src = 'https://www.google.com/recaptcha/enterprise.js?render=' + encodeURIComponent(key);
		script.async = true;
		script.defer = true;
		script.onload = () => {
			if (window.grecaptcha?.enterprise?.ready) {
				window.grecaptcha.enterprise.ready(() => resolve());
			} else {
				resolve();
			}
		};
		script.onerror = () => reject(new Error('recaptcha script failed to load'));

		document.head.appendChild(script);
	});
}

function sanitizeAction(url: string): string {
	const withoutQuery = url.split('?')[0];
	const segments = withoutQuery.split('/').filter((s) => s.length > 0);
	const last = segments.pop() ?? '';
	const cleaned = last.replace(/[^A-Za-z_/]/g, '_');
	return cleaned.length > 0 ? cleaned : 'api_call';
}

function isOpenProxyRequest(url: string | undefined): boolean {
	// All open-proxy endpoints (calculator, consent, legacy house/holiday) share
	// the `openserviceproxy` substring; the authenticated `serviceproxy` path
	// (e.g. `/api/v2/serviceproxy`) does not, and must not receive a token.
	// `openProxyBaseUrl` is still required as the CMS opt-in gate.
	return !!url && !!openProxyBaseUrl && url.toLowerCase().includes('openserviceproxy');
}
